import React from 'react';

import classnames from 'classnames';
import get from 'lodash.get';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { createSelector } from 'reselect';

import { UncontrolledCollapsibleListSection } from 'ui-components/components/collapsible-list/collapsible-list-section/uncontrolled';
import { LabeledInfo } from 'ui-components/components/labeled-info';

import ShipTo from '../../../../components/ship-to';
import UserDateFormat from '../../../../components/user-date-format/index';
import RefundDetailsSection from '../../../refund-details-section';
import { messages } from './messages';

// UI Components

// Services
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { FulfillmentPlannerContext } from '../../../../fulfillment-planner/context';
import { EntitiesAdapterForFulfillmentPlannerContext } from '../../../../fulfillment-planner';
import SingleOutboundShipmentDisplayContainer from '../../../../fulfillment-plans/shipments-display/single-outbound-shipment-display/connected-order-context';
import FulfillmentPlannerContextDrivenOtherShippingOptions from '../../../../fulfillment-planner/other-shipping-options';
import { OrderPropType } from '../../../../prop-types/order';
import { SHIPMENT_DETAILS } from '../../../../constants/component-names';
import { FulfillmentPlanService } from '../../../../services/fulfillment-plan-service';

// Default PropTypes

// PropTypes

// Components
import CustomsWidgetContainer from '../../../customs-declarations/container';
import { CustomsDocumentsModalUploadSection } from '../customs-documents-modal-upload-section';
import SellerPresetsHotkey from '../../../hotkeys/seller-presets';
import { ShipmentDetailsFulfillmentConfiguration } from './fulfillment-configuration';
import { ShipmentDetailsItems } from './items';
import style from './shipment-details-modal-content.module.less';

const selectPresetFulfillmentPlanId = createSelector(
  (fulfillmentPlanId) => fulfillmentPlanId,
  (fulfillmentPlanId) => (fulfillmentPlanId ? [fulfillmentPlanId] : emptyArray),
);

const renderShipmentWidget = (fulfillmentPlanId) => (
  <>
    <FulfillmentPlannerContext.Consumer>
      {({ applyPresetConfiguration }) => (
        <SellerPresetsHotkey fulfillmentPlanIds={selectPresetFulfillmentPlanId(fulfillmentPlanId)} onApplyPreset={applyPresetConfiguration}>
          <ShipmentDetailsFulfillmentConfiguration fulfillmentPlanId={fulfillmentPlanId} />
        </SellerPresetsHotkey>
      )}
    </FulfillmentPlannerContext.Consumer>
    <CustomsWidgetContainer fulfillmentPlanId={fulfillmentPlanId} readOnly={false} />
    <FulfillmentPlannerContextDrivenOtherShippingOptions />
  </>
);

export const ShipmentDetailsBody = ({
  fulfillmentPlan = emptyObject,
  fulfillments = emptyArray,
  salesOrders = emptyArray,
  hasActiveLabel = false,
  actions = emptyObject,
  isRefundAssistView = false,
}) => {
  const intl = useIntl();
  const isExternal = FulfillmentPlanService.isExternal(fulfillmentPlan);
  const isManual = FulfillmentPlanService.isManual(fulfillmentPlan);
  const items = get(fulfillmentPlan, 'items', emptyArray);
  const externalFulfillmentConfig = get(fulfillmentPlan, 'externalFulfillmentConfiguration.shipTo');

  const shipTo = isExternal || isManual ? externalFulfillmentConfig : get(fulfillmentPlan, 'shipTo');
  const fulfillmentPlanId = get(fulfillmentPlan, 'fulfillmentPlanId');
  const { orderNumbers, shipByDateTimes, requestedServices } = salesOrders.reduce(
    (acc, order) => {
      acc.orderNumbers.push(order.orderNumber);
      if (order.shipByDateTime) {
        acc.shipByDateTimes.push(order.shipByDateTime);
      }
      if (order.requestedService) {
        acc.requestedServices.push(order.requestedService);
      }
      return acc;
    },
    { orderNumbers: [], shipByDateTimes: [], requestedServices: [] },
  );

  const earliestShipDateTime = shipByDateTimes
    .filter((dateTime) => !!dateTime)
    .reduce((earliestDate, shipDateTime) => {
      if (moment(earliestDate).isBefore(shipDateTime)) {
        return earliestDate;
      }
      return shipDateTime;
    }, null);

  return (
    <div className={style['content-body']}>
      <div className={style['content-left-section']}>
        <div className={style['content-left-section-content']}>
          <div className={style['content-order-info']}>
            <div className={style['content-order-info-top']}>
              <div className={style['order-info-left-side']}>
                <div className={style['content-whole-line']}>
                  <LabeledInfo className={style['order-number']} label={intl.formatMessage(messages.orderNumber)} showMdash={!orderNumbers}>
                    {orderNumbers.join(', ')}
                  </LabeledInfo>
                </div>
                <div className={style['content-shipby-batch']}>
                  <div className={style['content-split-line']}>
                    <LabeledInfo label={intl.formatMessage(messages.shipByDate)} showMdash={!earliestShipDateTime}>
                      <UserDateFormat value={earliestShipDateTime} />
                    </LabeledInfo>
                  </div>
                </div>
                <div className={style['content-whole-line']}>
                  <LabeledInfo label={intl.formatMessage(messages.serviceRequested)} showMdash={!requestedServices.length}>
                    {requestedServices.join(', ')}
                  </LabeledInfo>
                </div>
              </div>
              <div className={style['order-info-right-side']}>
                <ShipTo
                  className={style['ship-to-left-border']}
                  data={shipTo}
                  fulfillmentPlanId={fulfillmentPlanId}
                  hideAddressValidation
                  validateAddress={actions.validateAddress}
                />
              </div>
            </div>
            <ShipmentDetailsItems fulfillmentPlanId={fulfillmentPlanId} items={items} salesOrders={salesOrders} />
            {hasActiveLabel && fulfillments[0]?.fulfillmentId && (
              <CustomsDocumentsModalUploadSection fulfillmentPlanId={fulfillmentPlanId} shipmentId={fulfillments[0].fulfillmentId} />
            )}
          </div>
        </div>
      </div>
      <div className={style['content-right-section']}>
        <div
          className={classnames(style['content-right-section-content'], {
            [style['padded-content-right-section']]: hasActiveLabel,
          })}
        >
          <EntitiesAdapterForFulfillmentPlannerContext
            key={fulfillmentPlanId}
            fulfillmentPlanId={fulfillmentPlanId}
            isReadOnly={hasActiveLabel}
            parentComponentName={SHIPMENT_DETAILS}
          >
            {isRefundAssistView && fulfillmentPlanId && (
              <UncontrolledCollapsibleListSection
                alwaysOpen
                className={style['expandable-section']}
                header='Refund Details'
                noIcon
                theme='white'
              >
                <div className={style['shipment-display-wrapper']}>
                  <RefundDetailsSection fulfillment={fulfillments[0]} />
                </div>
              </UncontrolledCollapsibleListSection>
            )}
            {!hasActiveLabel && fulfillmentPlanId && renderShipmentWidget(fulfillmentPlan.fulfillmentPlanId)}
            {hasActiveLabel && fulfillmentPlanId && (
              <React.Fragment>
                <UncontrolledCollapsibleListSection
                  alwaysOpen
                  className={style['expandable-section']}
                  header={intl.formatMessage(messages.shipmentHeader)}
                  noIcon
                  theme='white'
                >
                  <div className={style['shipment-display-wrapper']}>
                    <SingleOutboundShipmentDisplayContainer fulfillmentPlanId={fulfillmentPlanId} />
                  </div>
                </UncontrolledCollapsibleListSection>
                <CustomsWidgetContainer
                  className={style['expandable-section']}
                  fulfillmentPlanId={fulfillmentPlanId}
                  noIcon
                  open
                  readOnly
                />
                <FulfillmentPlannerContextDrivenOtherShippingOptions className={style['expandable-section']} isReadOnly noIcon open />
              </React.Fragment>
            )}
          </EntitiesAdapterForFulfillmentPlannerContext>
        </div>
      </div>
    </div>
  );
};

ShipmentDetailsBody.propTypes = {
  fulfillmentPlan: PropTypes.object,
  fulfillments: PropTypes.array,
  salesOrders: PropTypes.arrayOf(OrderPropType),
  hasActiveLabel: PropTypes.bool,
  actions: PropTypes.object,
  isRefundAssistView: PropTypes.bool,
};
